import React, { useEffect, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { DispatchDetailBatch, tripKindOf } from '@/services/api/dispatches';

import { TripKindBadge } from '../components/DispatchBadges';

interface MergeTripModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The trip being merged AWAY (its stops move out; it's then deleted). */
  sourceBatch: DispatchDetailBatch | null;
  /** The same rider's other PLANNED trips — the merge targets. */
  candidateBatches: DispatchDetailBatch[];
  /** Rider that owns the trips — shown for context. */
  riderName?: string | null;
  capWeightKg: number;
  capVolumeM3: number;
  onSubmit: (targetBatch: DispatchDetailBatch) => Promise<void>;
}

const fmtWeight = (kg: number) => `${kg.toFixed(2)} kg`;
const fmtVolume = (m3: number) => `${m3.toFixed(3)} m³`;

/** Combined-load meter for one dimension after the two trips merge. */
const ProjBar: React.FC<{
  label: string;
  projected: number;
  cap: number;
  format: (n: number) => string;
}> = ({ label, projected, cap, format }) => {
  const pct = cap > 0 ? projected / cap : 0;
  const over = pct > 1;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between text-[10px] tabular-nums">
        <span className="text-text-sub-600">{label}</span>
        <span
          className={
            over ? 'font-bold text-red-600' : 'font-semibold text-text-sub-600'
          }
        >
          {Math.round(pct * 100)}%
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-soft-200">
        <div
          className={`h-full ${over ? 'bg-red-500' : 'bg-accent/70'}`}
          style={{ width: `${Math.min(100, pct * 100)}%` }}
        />
      </div>
      <span className="text-[10px] text-text-soft-400 tabular-nums">
        {format(projected)} / {format(cap)}
      </span>
    </div>
  );
};

/**
 * Merge one trip into another trip of the SAME rider. Implemented as a bulk
 * stop move: every stop of the source batch is reassigned to the target batch
 * (`stopReassignments[].toBatchId`), which empties and deletes the source and
 * re-routes the target. No new endpoint. No capacity guard — the combined trip
 * can exceed 100%; that's only flagged here, not blocked.
 */
const MergeTripModal: React.FC<MergeTripModalProps> = ({
  isOpen,
  onClose,
  sourceBatch,
  candidateBatches,
  riderName,
  capWeightKg,
  capVolumeM3,
  onSubmit,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelectedId(null);
  }, [isOpen]);

  const targets = candidateBatches.filter((b) => b.id !== sourceBatch?.id);
  // A pickup run and a drop run never merge — they start/end at opposite
  // ends of the hub/pitstop. Such targets stay listed but locked, so ops sees
  // WHY the trip they expected isn't offered. The BE enforces the same rule
  // (TRIP_KIND_MISMATCH).
  const sourceKind = sourceBatch ? tripKindOf(sourceBatch.stops) : null;
  const isCompatible = (b: DispatchDetailBatch): boolean => {
    const targetKind = tripKindOf(b.stops);
    return sourceKind === null || targetKind === null || targetKind === sourceKind;
  };
  const compatibleCount = targets.filter(isCompatible).length;
  const selected = targets.find((b) => b.id === selectedId);
  const canSubmit =
    !submitting &&
    sourceBatch != null &&
    selected != null &&
    isCompatible(selected);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await onSubmit(selected);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-git-merge-line" />}
      iconTone="primary"
      title="Gabung trip"
      subtitle={
        sourceBatch
          ? `${
              sourceBatch.tripSequence != null
                ? `Trip #${sourceBatch.tripSequence}`
                : sourceBatch.code
            } · ${sourceBatch.stopCount} stop · ${sourceBatch.itemCount} item${
              riderName ? ` · ${riderName}` : ''
            }`
          : undefined
      }
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Batal
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Menggabung…' : 'Gabung trip'}
          </Button>
        </div>
      }
    >
      <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
        Gabung ke trip
      </p>
      {targets.length === 0 ? (
        <div className="rounded-lg border border-stroke-soft-200 px-3 py-4 text-center text-xs text-text-sub-600">
          {riderName ?? 'Rider ini'} cuma punya satu trip PLANNED — tidak ada
          trip lain untuk digabung.
        </div>
      ) : (
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {compatibleCount === 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
              Tidak ada trip sejenis untuk digabung — trip{' '}
              {sourceKind === 'PICKUP' ? 'pickup' : 'dropoff'} hanya bisa
              digabung dengan trip{' '}
              {sourceKind === 'PICKUP' ? 'pickup' : 'dropoff'} lain.
            </div>
          )}
          {targets.map((b) => {
            const checked = selectedId === b.id;
            const compatible = isCompatible(b);
            const projWeight = b.totalWeightKg + (sourceBatch?.totalWeightKg ?? 0);
            const projVolume = b.totalVolumeM3 + (sourceBatch?.totalVolumeM3 ?? 0);
            const over =
              (capWeightKg > 0 && projWeight > capWeightKg) ||
              (capVolumeM3 > 0 && projVolume > capVolumeM3);
            return (
              <label
                key={b.id}
                className={`block rounded-lg border px-3 py-2.5 transition-colors ${
                  !compatible
                    ? 'cursor-not-allowed border-stroke-soft-200 opacity-60'
                    : checked
                      ? 'cursor-pointer border-accent bg-bg-weak-50'
                      : 'cursor-pointer border-stroke-soft-200 hover:bg-bg-weak-50'
                }`}
                title={
                  compatible
                    ? undefined
                    : 'Beda jenis trip — trip dropoff dan trip pickup tidak bisa digabung'
                }
              >
                <div className="flex items-center gap-2.5">
                  <input
                    type="radio"
                    name="merge-trip-target"
                    checked={checked}
                    onChange={() => setSelectedId(b.id)}
                    disabled={submitting || !compatible}
                    className="h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-semibold tracking-tight text-text-strong-950">
                        {b.tripSequence != null
                          ? `Trip #${b.tripSequence}`
                          : b.code}
                      </span>
                      <TripKindBadge stops={b.stops} />
                      {!compatible && (
                        <Badge status="warning" size="sm">
                          beda jenis trip
                        </Badge>
                      )}
                      {over && (
                        <Badge status="error" size="sm">
                          overload
                        </Badge>
                      )}
                    </div>
                    <span className="font-mono text-[10px] text-text-soft-400 tabular-nums">
                      {b.code} · {b.stopCount} stop · {b.itemCount} item
                    </span>
                  </div>
                </div>
                {/* Projected combined load = target + source. */}
                <div className="mt-2 grid grid-cols-2 gap-3 pl-6">
                  <ProjBar
                    label="Weight (gabungan)"
                    projected={projWeight}
                    cap={capWeightKg}
                    format={fmtWeight}
                  />
                  <ProjBar
                    label="Volume (gabungan)"
                    projected={projVolume}
                    cap={capVolumeM3}
                    format={fmtVolume}
                  />
                </div>
              </label>
            );
          })}
        </div>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-text-sub-600">
        Semua stop trip ini dipindah ke trip terpilih, trip ini lalu dihapus,
        dan rutenya dihitung ulang otomatis. Hanya trip sejenis yang bisa
        digabung (dropoff dengan dropoff, pickup dengan pickup). Tidak ada batas
        kapasitas — hasil gabungan yang{' '}
        <span className="font-semibold">overload</span> tetap diterima, hanya
        ditandai di sini.
      </p>
    </FormModal>
  );
};

export default MergeTripModal;
